import {
  ChangeEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';

import { Box, Button, Popover, PopoverItem, SearchBar, Select } from '@myn/mui';

import {
  Co2Filter,
  OffsetSubordersMeta,
} from '../../types/co2notifications.js';

export type Props = {
  filter: Co2Filter;
  ordersMeta?: OffsetSubordersMeta;
  onChange: (filter: Co2Filter) => void;
};

const Co2NotificationsHeader = ({ filter, ordersMeta, onChange }: Props) => {
  const { t } = useTranslation('@myn/planning');

  const [searchValue, setSearchValue] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const open = Boolean(anchorEl);

  const handleClickSortBy = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const debouncedValue = useDebounce(searchValue, 500);

  useEffect(() => {
    onChange({ ...filter, search: debouncedValue });
  }, [debouncedValue]);

  const handleShowFilterChange = (option: string) => {
    onChange({ ...filter, show: { name: filter.show.name, value: option } });
  };

  const handleSortByFilterChange = (option: string) => {
    onChange({
      ...filter,
      sortBy: { name: filter.sortBy.name, value: option },
    });
    setAnchorEl(null);
  };

  const showFilterOptions = useMemo(
    () =>
      ordersMeta?.filters.statusFilters.map((option) => ({
        value: option.name,
        label: option.label,
      })) ?? [],
    [ordersMeta],
  );

  const sortByFilterOptions = useMemo(
    () =>
      ordersMeta?.filters.sortBy.map((option) => ({
        label: option.label,
        value: option.name,
      })) ?? [],
    [ordersMeta],
  );

  return (
    <Box
      display="grid"
      gridTemplateColumns="2fr 6fr 1fr"
      gap={(theme) => theme.spacing(1)}
    >
      <Select
        aria-label="show filter"
        options={showFilterOptions}
        onChange={handleShowFilterChange}
        startIcon="Filter"
        value={filter.show.value}
      />

      <SearchBar
        aria-label="Search"
        onChange={handleSearch}
        placeholder={t('Search')}
        fullWidth
        value={searchValue}
        onClear={() => setSearchValue('')}
      />

      <Button
        aria-label="Sort by"
        size="large"
        startIcon="SortDescending"
        variant="text"
        onClick={handleClickSortBy}
      >
        {t('Sort by')}
      </Button>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {sortByFilterOptions.map((option) => (
          <PopoverItem
            key={option.value}
            text={option.label}
            onClick={() => handleSortByFilterChange(option.value)}
            size="large"
          />
        ))}
      </Popover>
    </Box>
  );
};

export default memo(Co2NotificationsHeader);
